// deps
import classNames from "classnames";
import React, { memo, ReactElement, useCallback, useMemo, useState } from "react";
import { format, useEffectOnce } from "@swiggy-private/core-ui";

// internal deps
import { PAYLATER_TYPES, User, isEmpty } from "@swiggy-private/payment-core";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// helpers
import {
    canEnablePaymentMethod,
    getPaymentAmount,
    getPaymentMethodDisableMsg,
    getPaymentMethodDiscount,
    getPaymentMethodDisplayName,
    getPaymentMethodIconUrl,
    getPaymentMethodPromoMsg,
} from "@payment-ui/helpers/Payment";
import { getPaymentDueDateMessage, isAmazonPayLater } from "@payment-ui/helpers/PayLater";

// interfaces
import { ApiFetchState as FetchState } from "@swiggy-private/payment-core";
import { IPayLaterPaymentGroup, PayLaterPaymentMethodData } from "@payment-ui/interfaces/PayLater";
import { PaymentMethodProps } from "@payment-ui/interfaces";

// widgets
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";

// components
import LinkStatus from "./components/LinkStatus";

// test-ids
import { getPayLaterTestIds } from "@swiggy-private/payment-test-ids";
import styles from "./PayLater.v4.scss";

export interface PayLaterProps extends PaymentMethodProps {
    key: string;
    methodData: PayLaterPaymentMethodData;
    paymentActions: IPayLaterPaymentGroup["paymentActions"];
    user: User;
    paymentAmount: string;
}

function PayLaterPaymentMethod(props: PayLaterProps): ReactElement {
    const [canShowLinkDialog, setCanShowLinkDialog] = useState(false);

    const enabled = useMemo(
        () => canEnablePaymentMethod(props.methodData, props.isPaymentGroupEnabled),
        [props.isPaymentGroupEnabled, props.methodData],
    );
    const { checkPayLaterLinkStatus, onSelect, onPay, linkPayLater, analyticsPayLater, onKeyFactStatementClick } =
        props.paymentActions;
    const { paylaterMethodImpression, linkAccountImpression, onClickLink } = analyticsPayLater;
    const [fetchState, payLaterLinkedData] = checkPayLaterLinkStatus(props.methodData);
    const globalEnabled = enabled && payLaterLinkedData.enabled !== false;
    const payLaterTestIds = useMemo(
        () => getPayLaterTestIds(props.methodData.paymentCode),
        [props.methodData.paymentCode],
    );

    // to send impression event
    useEffectOnce(() => {
        paylaterMethodImpression(
            props.methodData.paymentCode,
            !props.methodData?.enabled ? props?.methodData?.meta?.disableMessage : "",
        );
        if (!payLaterLinkedData?.linked) linkAccountImpression(props.methodData.paymentCode);
    });

    // handle click on link method
    const onClick = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();
            if (!globalEnabled || fetchState !== FetchState.SUCCESS) {
                return;
            }

            if (!payLaterLinkedData?.linked) {
                onClickLink(props.methodData.paymentCode);
                if (props.methodData.paymentCode === PAYLATER_TYPES.LAZYPAY) {
                    setCanShowLinkDialog(true);
                } else {
                    linkPayLater.onProceed(props.methodData.paymentCode, props.methodData?.meta?.simplRedirectionUrl);
                }
                return;
            }

            onSelect(props.methodData);
        },
        [fetchState, payLaterLinkedData, props.methodData, onSelect, globalEnabled, linkPayLater, onClickLink],
    );
    const closeLinkAccountDialog = useCallback((): void => {
        setCanShowLinkDialog(false);
    }, []);

    // handles pay click of lazy pay method
    const onPayClick = useCallback(
        (e: React.MouseEvent): void => {
            e.stopPropagation();
            if (fetchState !== FetchState.SUCCESS) {
                return;
            }
            onPay(props.methodData);
        },
        [fetchState, onPay, props.methodData],
    );

    const isAplMethod = isAmazonPayLater(props.methodData.paymentCode);
    const formattedCreditLimit = payLaterLinkedData?.creditLimit
        ? Number(payLaterLinkedData.creditLimit).toLocaleString("en-IN")
        : undefined;

    const promoMsg = useMemo(() => {
        if (globalEnabled) {
            if (isAplMethod && payLaterLinkedData?.linked && formattedCreditLimit) {
                return format(TEXTS.AVAILABLE_CREDIT_X, formattedCreditLimit);
            }
            return getPaymentMethodPromoMsg(props.methodData);
        }

        if (enabled) {
            return payLaterLinkedData.disableMessage;
        }

        return getPaymentMethodDisableMsg(props.methodData);
    }, [enabled, globalEnabled, payLaterLinkedData, props.methodData, isAplMethod, formattedCreditLimit]);

    const renderIcon = useCallback(
        (width?: number): ReactElement => (
            <PaymentMethodIcon
                src={getPaymentMethodIconUrl(props.methodData)}
                width={width}
                data-testid={payLaterTestIds.ICON}
            />
        ),
        [props.methodData, payLaterTestIds],
    );

    const renderIconV4 = useCallback(
        (width?: number): ReactElement => (
            <PaymentMethodIcon
                src={getPaymentMethodIconUrl(props.methodData)}
                width={width}
                imgClassName={styles.payLaterLinkHeaderIconImg}
                parentClassName={styles.payLaterLinkHeaderIcon}
                data-testid={payLaterTestIds.ICON}
            />
        ),
        [props.methodData, payLaterTestIds.ICON],
    );

    const containerClass = classNames(styles.paymentMethod, {
        [styles.paymentMethodDisabled]: !globalEnabled,
    });

    const dueDateMessage = getPaymentDueDateMessage(props.methodData);

    const handleKeyFactsClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (payLaterLinkedData?.keyFactStatementUrl && onKeyFactStatementClick) {
                onKeyFactStatementClick(payLaterLinkedData.keyFactStatementUrl);
            }
        },
        [payLaterLinkedData?.keyFactStatementUrl, onKeyFactStatementClick],
    );

    const showKeyFactsDisclaimer =
        isAplMethod && payLaterLinkedData?.linked && !isEmpty(payLaterLinkedData.keyFactStatementUrl);

    return (
        <div className={containerClass} onClick={onClick} data-testid={payLaterTestIds.CONTAINER}>
            <div className={styles.paymentMethodContainer}>
                {renderIcon()}
                <PaymentMethodName
                    displayName={getPaymentMethodDisplayName(props.methodData)}
                    promoMsg={promoMsg}
                    discount={getPaymentMethodDiscount(props.methodData)}
                    enabled={globalEnabled}
                    data-testid={payLaterTestIds.NAME}
                />

                {globalEnabled && (
                    <LinkStatus
                        user={props.user}
                        renderIcon={renderIconV4}
                        loading={fetchState === FetchState.LOADING}
                        selected={props.selected}
                        methodData={props.methodData}
                        payLaterLinkedData={payLaterLinkedData}
                        linkAccountActions={props.paymentActions.linkAccountActions}
                        payLaterTestIds={payLaterTestIds}
                        canShowDialog={canShowLinkDialog}
                        closeDialog={closeLinkAccountDialog}
                    />
                )}
            </div>

            {globalEnabled
                ? props.selected &&
                  payLaterLinkedData.linked && (
                      <div className={styles.paymentMethodPayButton}>
                          {showKeyFactsDisclaimer && (
                              <div className={styles.keyFactsDisclaimer}>
                                  <Text variant="V2.body3.neutral.regular" color="text_Med_Emphasis">
                                      {TEXTS.KEY_FACTS_DISCLAIMER}
                                      <span
                                          className={styles.keyFactsLink}
                                          role="link"
                                          tabIndex={0}
                                          onClick={handleKeyFactsClick}
                                      >
                                          {TEXTS.KEY_FACTS_STATEMENT}
                                      </span>
                                      .
                                  </Text>
                              </div>
                          )}
                          <SuperButton
                              buttonType="specialPositive"
                              variant="fullwidth"
                              buttonSize="medium"
                              onClick={onPayClick}
                              data-testid={payLaterTestIds.PAY_BUTTON}
                          >
                              {format(TEXTS.PAY_X, getPaymentAmount(props.paymentAmount, props.methodData))}
                          </SuperButton>
                          {!isEmpty(dueDateMessage) && (
                              <div className={styles.payLaterDueDate} data-testid={payLaterTestIds.DUE_DATE_MESSAGE}>
                                  {dueDateMessage}
                              </div>
                          )}
                      </div>
                  )
                : null}
        </div>
    );
}

export default memo(PayLaterPaymentMethod);
